/**
 * useInProgressJourney — détecte un parcours funnel commencé mais non finalisé.
 *
 * Scanne les stores persistés (localStorage zustand) des funnels pour proposer
 * une reprise (ResumeJourneyBanner) et adapter d'autres CTA.
 *
 * Un parcours est "en cours" s'il a des données significatives ET pas de leadId
 * (donc non soumis). On ignore les parcours vides.
 */

import { useEffect, useState } from 'react';

export interface InProgressJourney {
  funnel: 'diagnostic' | 'aids_calculator' | 'agri_diagnostic' | 'comparateur';
  label: string;
  href: string;
  /** Libellé court de l'étape/contexte atteint, pour rassurer ("Étape X", entreprise…). */
  hint?: string;
}

/** Tronque proprement un libellé long (raison sociale…) pour le bandeau compact. */
function shortLabel(value: string, max = 22): string {
  const trimmed = value.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max - 1).trimEnd()}…` : trimmed;
}

/** Lit un store zustand persisté ({ state, version }) de façon défensive. */
function readZustand(key: string): Record<string, unknown> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: Record<string, unknown> };
    return parsed?.state ?? null;
  } catch {
    return null;
  }
}

function detect(): InProgressJourney | null {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname;

  // ── Comparateur (token session) ──
  try {
    const token = sessionStorage.getItem('sef_comparateur_resume_token');
    if (token && !path.includes('/comparateur')) {
      return {
        funnel: 'comparateur',
        label: 'Comparateur énergie en cours',
        href: `/comparateur/reprendre/${encodeURIComponent(token)}`,
        hint: 'Reprise en 1 clic',
      };
    }
  } catch {
    /* ignore */
  }

  // ── Calculer mes aides ──
  const aids = readZustand('sef_aids_funnel_v1');
  if (aids && !aids.leadId && typeof aids.step === 'number' && aids.step > 0) {
    if (!path.includes('/calculer-mes-aides')) {
      return {
        funnel: 'aids_calculator',
        label: "Calcul d'aides en cours",
        href: '/calculer-mes-aides',
        hint: `Étape ${Number(aids.step) + 1}/6`,
      };
    }
  }

  // ── Diagnostic CEE ──
  const diag = readZustand('sef_diagnostic');
  if (diag && !diag.leadId) {
    const hasAnswers = Array.isArray(diag.answers) && diag.answers.length > 0;
    const hasSiret = typeof diag.siret === 'string' && diag.siret.length > 0;
    if ((hasAnswers || hasSiret) && !path.includes('/diagnostic')) {
      const company =
        typeof diag.companyName === 'string' && diag.companyName ? diag.companyName : null;
      return {
        funnel: 'diagnostic',
        label: 'Diagnostic CEE en cours',
        href: '/diagnostic',
        hint: company ? shortLabel(company) : 'Sauvegardé automatiquement',
      };
    }
  }

  // ── Diagnostic agricole ──
  const agri = readZustand('sef_agri_diagnostic_v2');
  if (agri && !agri.leadId && agri.orientation) {
    if (!path.includes('/agriculteurs/diagnostic')) {
      return {
        funnel: 'agri_diagnostic',
        label: 'Diagnostic agricole en cours',
        href: '/agriculteurs/diagnostic-cee',
        hint: 'Sauvegardé automatiquement',
      };
    }
  }

  return null;
}

export function useInProgressJourney(): InProgressJourney | null {
  const [journey, setJourney] = useState<InProgressJourney | null>(null);

  useEffect(() => {
    setJourney(detect());
    const onNavigate = () => setJourney(detect());
    window.addEventListener('vike:navigate', onNavigate);
    window.addEventListener('popstate', onNavigate);
    return () => {
      window.removeEventListener('vike:navigate', onNavigate);
      window.removeEventListener('popstate', onNavigate);
    };
  }, []);

  return journey;
}
